/**
 * gridcode Excel custom functions.
 *
 * Functions are exposed in Excel as `=GP.<NAME>(...)` (namespace `GP` set in manifest.xml).
 *
 * Bundled with `tsup` into `public/functions.js` (single IIFE), then loaded by Office's
 * Custom Functions runtime at https://localhost:3000/functions.html.
 */

import {
  encodeDigipin,
  decodeDigipin,
  encodePluscode,
  decodePluscode,
  encodeGeohash,
  decodeGeohash,
  addChecksum,
  verifyChecksum,
  convert,
  distance,
  type GridSystem,
  type DigipinPrecision,
  type PluscodeLength,
} from "../../src/index.js";

// CustomFunctions is a global provided by the Office Custom Functions runtime
// (loaded via <script> in functions.html before this bundle).
declare const CustomFunctions: {
  associate: (id: string, fn: (...args: unknown[]) => unknown) => void;
};

function asGridSystem(s: string): GridSystem {
  const lower = s.trim().toLowerCase();
  if (lower !== "digipin" && lower !== "pluscode" && lower !== "geohash") {
    throw new Error(`Unknown system '${s}'. Use digipin, pluscode, or geohash.`);
  }
  return lower as GridSystem;
}

/**
 * Encode lat/lng to a DIGIPIN code (India only — bbox lat 2.5-38.5, lng 63.5-99.5).
 * @customfunction DIGIPIN
 * @param lat Latitude
 * @param lng Longitude
 * @param precision [OPTIONAL] Code length 4-10 (default 10 ≈ 4m × 4m cell)
 * @returns DIGIPIN code as text
 */
function digipin(lat: number, lng: number, precision?: number): string {
  return encodeDigipin(lat, lng, (precision ?? 10) as DigipinPrecision);
}

/**
 * Decode a DIGIPIN code → latitude of the cell centre.
 * @customfunction DIGIPIN_LAT
 * @param code DIGIPIN code (with or without dashes)
 * @returns Latitude
 */
function digipinLat(code: string): number {
  return decodeDigipin(code).center.lat;
}

/**
 * Decode a DIGIPIN code → longitude of the cell centre.
 * @customfunction DIGIPIN_LNG
 * @param code DIGIPIN code (with or without dashes)
 * @returns Longitude
 */
function digipinLng(code: string): number {
  return decodeDigipin(code).center.lng;
}

/**
 * Encode lat/lng to a Plus Code / Open Location Code (works globally).
 * @customfunction PLUSCODE
 * @param lat Latitude
 * @param lng Longitude
 * @param length [OPTIONAL] Code length 4, 6, 8, 10, 11, or 12 (default 10 ≈ 14m × 14m cell)
 * @returns Plus Code as text
 */
function pluscode(lat: number, lng: number, length?: number): string {
  return encodePluscode(lat, lng, (length ?? 10) as PluscodeLength);
}

/**
 * Encode lat/lng to a GeoHash (works globally).
 * @customfunction GEOHASH
 * @param lat Latitude
 * @param lng Longitude
 * @param length [OPTIONAL] Code length 1-12 (default 9 ≈ 5m × 5m cell)
 * @returns GeoHash as text
 */
function geohash(lat: number, lng: number, length?: number): string {
  return encodeGeohash(lat, lng, length ?? 9);
}

/**
 * Re-encode a code from one grid system to another via lat/long pivot.
 * @customfunction RECODE
 * @param code Source code
 * @param from_system Source system: "digipin" | "pluscode" | "geohash"
 * @param to_system Target system: "digipin" | "pluscode" | "geohash"
 * @param precision [OPTIONAL] Target precision/length
 * @returns Code in the target system
 */
function recode(
  code: string,
  from_system: string,
  to_system: string,
  precision?: number,
): string {
  return convert(code, asGridSystem(from_system), asGridSystem(to_system), precision ? { precision } : {});
}

/**
 * Append a single-character Luhn-mod-16 checksum to a DIGIPIN code (catches typos).
 * @customfunction CHECKSUM
 * @param code DIGIPIN code
 * @returns DIGIPIN code with -X checksum suffix
 */
function checksum(code: string): string {
  return addChecksum(code);
}

/**
 * Verify a DIGIPIN code's trailing checksum digit.
 * @customfunction VERIFY
 * @param code_with_checksum DIGIPIN code with -X checksum suffix
 * @returns TRUE if checksum matches, FALSE otherwise
 */
function verify(code_with_checksum: string): boolean {
  return verifyChecksum(code_with_checksum);
}

/**
 * Distance in metres between the centres of two cells. Systems can differ.
 * @customfunction DISTANCE
 * @param codeA First code
 * @param codeB Second code
 * @param systemA System of codeA: "digipin" | "pluscode" | "geohash"
 * @param systemB System of codeB
 * @returns Distance in metres (Haversine, great-circle)
 */
function distanceFn(codeA: string, codeB: string, systemA: string, systemB: string): number {
  return distance(codeA, asGridSystem(systemA), codeB, asGridSystem(systemB));
}

// Register every function with the Office runtime.
// The id (string) here MUST match the id in functions.json.
CustomFunctions.associate("DIGIPIN", digipin);
CustomFunctions.associate("DIGIPIN_LAT", digipinLat);
CustomFunctions.associate("DIGIPIN_LNG", digipinLng);
CustomFunctions.associate("PLUSCODE", pluscode);
CustomFunctions.associate("GEOHASH", geohash);
CustomFunctions.associate("RECODE", recode);
CustomFunctions.associate("CHECKSUM", checksum);
CustomFunctions.associate("VERIFY", verify);
CustomFunctions.associate("DISTANCE", distanceFn);
